<template>
  <CustomModal
    :is-modal-open="isOpen"
    @toggleModal="closeModal"
    @onClickAway="closeModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[588px] tw:md:w-[588px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader>
      <div class="tw:flex tw:items-center">
        <h3 class="tw:font-bold tw:text-xl tw:text-gray-700">{{ $t('rc_reactivate_campaign_button') }}</h3>
      </div>
    </template>

    <div class="tw:flex tw:flex-col tw:w-full" v-if="!existingPaymentMethodsLoading">
      <ResumeModalContent :defaultPaymentMethod="defaultPaymentMethod" v-if="hasAdminOrSupport" />
      <ResumeAsAdminContent v-else />
    </div>
    <template #modalFooter>
      <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-y-4 tw:justify-end tw:w-full">
        <GhostButton @click="closeModal" :disabled="isLoading">{{ $t('cancel') }}</GhostButton>
        <MainButton @click="restartCampaign" class="tw:md:ml-6" :disabled="isActionDisabled">
          <span v-show="!isLoading">{{
            hasAdminOrSupport ? $t('rc_reactivate_campaign_button') : $t('rc_admin_log_in_cta')
          }}</span>
          <span v-show="isLoading" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>
<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'

import CustomModal from '@/components/base/CustomModal.vue'
import ResumeModalContent from '@/components/rateConnect/resumeFlow/ResumeModalContent.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import ResumeAsAdminContent from './ResumeAsAdminContent.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'

import { useCampaignMutation } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useRateConnect } from '@/components/rateConnect/useRateConnect'
import { useExistingPaymentMethods } from '@/components/payment/queries'

import appAnalytics from '@/utils/tracking'
import { useSessionValues } from '@/composables/useSessionValues'

const store = useStore()
defineProps({
  isOpen: Boolean,
})
const { toggleResumeCampaign } = useRateConnect()
const { hasAdminOrSupport } = useSessionValues()
const closeModal = () => {
  appAnalytics.track(appAnalytics.events.RC_RESUME_CAMPAIGN_CANCEL_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    paymentMethod: defaultPaymentMethod.value?.type,
    isExpired: defaultPaymentMethod.value?.expired,
    expirationDate: `${defaultPaymentMethod.value?.expirationMonth}/${defaultPaymentMethod.value?.expirationYear}`,
  })

  toggleResumeCampaign('close')
}
const { displayNotification, showSaveError } = useToastNotifications()
const { resumeCampaign } = useCampaignMutation()
const { existingPaymentMethods, existingPaymentMethodsLoading } = useExistingPaymentMethods(
  selectedAccommodationId.value
)
const defaultPaymentMethod = computed(() => existingPaymentMethods.value?.find(item => item.isDefault))
const isActionDisabled = computed(() => {
  if (!hasAdminOrSupport.value) return false
  if (existingPaymentMethodsLoading.value) {
    return true
  }
  if (!defaultPaymentMethod.value) return false
  const method = defaultPaymentMethod.value
  return method ? method.expired || method.type === 'WIRE_TRANSFER' : true
})

const restartCampaign = () => {
  if (hasAdminOrSupport.value) {
    appAnalytics.track(appAnalytics.events.RC_RESUME_CAMPAIGN_CLICKED, {
      accommodationId: selectedAccommodationId.value,
      paymentMethod: defaultPaymentMethod.value?.type,
      isExpired: defaultPaymentMethod.value?.expired,
      expirationDate: `${defaultPaymentMethod.value?.expirationMonth}/${defaultPaymentMethod.value?.expirationYear}`,
    })
    resumeCampaign(
      {
        accommodationId: selectedAccommodationId.value,
      },
      {
        onSuccess: () => {
          toggleResumeCampaign('close')
          displayNotification({
            title: 'setting_updated_success',
            titleTranslationKey: true,
            message: 'rc_campaign_resumed_header',
            isTranslationKey: true,
            type: 'success',
          })
        },
        onError: () => {
          toggleResumeCampaign('close')
          showSaveError()
        },
      }
    )
  } else {
    appAnalytics.track(appAnalytics.events.RC_LOGIN_AS_ADMIN_CLICKED, {
      accommodationId: selectedAccommodationId.value,
    })
    store.dispatch('session/logout', '/rate-connect/running/performance?loginAsAdmin=true')
  }
}
</script>
